import produce from 'immer';
import { get, set } from 'lodash';
import { IAdvancedRichText, IBooleanCondition } from '../../../../../../inputs';
import { debug } from '../../../../../../utils/debug';
import { engineUuid } from '../../../../../../utils/uuid/engineUuid';
import { WidgetConditionManager } from '../../../../../../validator/WidgetConditionManager';
import { IReference } from '../../../../../expressions/references/IReference';
import {
  IVirtualReferencePaths,
  findVirtualReferencesInWidget,
} from '../../../../../expressions/references/virtual/findVirtualReferencesInWidget';
import { normalizeVirtualReference } from '../../../../../expressions/references/virtual/normalizeVirtualReference';
import { normalizeVirtualRefsRte } from '../../../../../expressions/references/virtual/normalizeVirtualRefsRte';
import { IBaseWidget } from '../../../../IBaseWidget';
import { ICodeWidget } from '../../../../ICodeWidget';
import { Widget } from '../../../../Widget';
import { TableCellType } from '../ITableCell';
import { ITableInputWidget } from '../ITableInputWidget';
import { materializeTableWidgetRowPrefix } from './materializeTableWidgetRowPrefix';

export function engineWidgetCellId(
  table: ITableInputWidget,
  rowId: string,
  columnId: string,
) {
  return engineUuid(`${table.id}.${rowId}.${columnId}`);
}

interface IMergeable {
  visible?: IBooleanCondition | undefined;
  enabled?: IBooleanCondition | undefined;
}

function mergeConditions(column: IMergeable, row: IMergeable) {
  return {
    visible: WidgetConditionManager.merge([column.visible, row.visible])
      ?.condition,
    enabled: WidgetConditionManager.merge([column.enabled, row.enabled])
      ?.condition,
  };
}

export function materializeTableInputWidgets(
  table: ITableInputWidget,
): Widget[] {
  const widgets: Widget[] = [];

  for (let r = 0; r < table.rows.length; r++) {
    const row = table.rows[r];

    if (row.customRow) {
      widgets.push(
        // Ritorniamo i widget della custom row così come sono configurati.
        // In caso cambiassimo idea e volessimo materializzarli, ricordiamoci
        // di aggiornare anche il `getTableWidgetAtCell`
        ...row.cells.flatMap(cell =>
          cell.type === TableCellType.Widget
            ? [
                {
                  ...cell.widget,
                  ...mergeConditions(cell.widget, row),
                },
              ]
            : [],
        ),
      );
      continue;
    }

    for (let c = 0; c < table.columns.length; c++) {
      const column = table.columns[c];
      const materializedRowPrefix = materializeTableWidgetRowPrefix(
        table,
        row,
        r,
      );

      const cell = {
        ...column,
        ...mergeConditions(column, row),
      };

      // Ci sono dei riferimenti che devono essere normalizzati poiché
      // virtuali, ovvero che fanno riferimento alla propria riga.
      const virtualColumnReferences = findVirtualReferencesInWidget(cell);
      if (virtualColumnReferences.referencesPaths.length > 0) {
        // TODO Al momento facciamo side-effect sul widget stesso della colonna.
        // Essendo un loop sulle righe, in realtà si comporta come un `or`
        column.__hasVirtualColumnRefs = true;
      }

      widgets.push({
        ...materializeReferencesInColumn(
          cell,
          materializedRowPrefix,
          virtualColumnReferences,
        ),
        fromVirtualId: column.id,
        id: engineWidgetCellId(table, row.id, column.id),
        code: `${materializedRowPrefix}_${column.code}`,
      } as Widget & ICodeWidget);
    }
  }

  // console.log(`[materializeTableInputWidgets] widgets:`, widgets);
  return widgets;
}

function materializeReferencesInColumn(
  column: IBaseWidget,
  prefix: string,
  virtualPaths: IVirtualReferencePaths,
) {
  return produce(column, draft => {
    for (const targetPath of virtualPaths.referencesPaths) {
      debug.log('virtualrefs.normalize', `From virtual`, { targetPath });
      const virtualRef = get(column, targetPath) as IReference;
      const normalizedRef = normalizeVirtualReference(virtualRef, prefix);
      debug.log('virtualrefs.normalize', `From virtual`, { virtualRef, normalizedRef }); // prettier-ignore
      set(draft, targetPath, normalizedRef);
    }

    for (const rtePath of virtualPaths.rtePaths) {
      const rte = get(column, rtePath) as IAdvancedRichText;
      set(
        draft,
        rtePath,
        rte.map(item => ({
          ...item,
          text: normalizeVirtualRefsRte(item.text, prefix),
        })),
      );
    }
  });
}
